import useRequest from "@/lib/request/useRequest";
import { createFileRoute } from "@tanstack/react-router";
import {
    ColumnDef,
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    Header,
    Row,
    useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useCallback, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowDownShortWide,
    faArrowUpShortWide,
    faEdit,
    faPlus,
    faSearch,
} from "@fortawesome/free-solid-svg-icons";
import {
    Input,
    Autocomplete,
    AutocompleteItem,
    Button,
    Spinner,
} from "@heroui/react";
import { Contract, Enrollment } from "@/types/field";
import useAuth from "@/lib/auth/useAuth";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import { Organization } from "@/types/organization";
import { EnrollmentTier, getEnrollmentTierLabel } from "@/lib/utils/enrollment";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { getColumnValues } from "@/lib/utils/table";
import classNames from "classnames";
import { formatCurrency } from "@/lib/utils/currency";
import ContractForm from "@/components/enrollment/ContractForm";
import dayjs, { Dayjs } from "dayjs";

export const Route = createFileRoute("/dashboard/enrollment/contracts")({
    component: RouteComponent,
});

type LineItem = {
    acres: number;
    retail_price: string;
    contracted_price: string;
    retail_total: string;
    contract_total: string;
    discount: string;
};

type SummaryItem = {
    date: string;
    type: "Enrollment" | "Unenrollment" | "Contract";
    contractId?: number;
    advanced?: LineItem;
    basic?: LineItem;
    advancedV2?: LineItem;
    complete?: LineItem;
    standard?: LineItem;
    irrigation?: LineItem;
    monitoring?: LineItem;
    preview?: LineItem;
    organization?: Organization;
};

function CurrencyCell({ value }: { value?: string | number }) {
    return (
        <div>
            {value !== null && value !== undefined
                ? formatCurrency(value)
                : "\u00A0"}
        </div>
    );
}

const ENROLLMENT_TIER_KEYS: Record<EnrollmentTier, string> = {
    [EnrollmentTier.Advanced]: "advanced",
    [EnrollmentTier.Basic]: "basic",
    [EnrollmentTier.AdvancedV2]: "advancedV2",
    [EnrollmentTier.Complete]: "complete",
    [EnrollmentTier.Standard]: "standard",
    [EnrollmentTier.Lite]: "lite",
    [EnrollmentTier.Irrigation]: "irrigation",
    [EnrollmentTier.Monitoring]: "monitoring",
    [EnrollmentTier.Preview]: "preview",
};

function getTotal(
    row: SummaryItem,
    key:
        | "acres"
        | "retail_price"
        | "contracted_price"
        | "retail_total"
        | "contract_total"
        | "discount",
) {
    return (
        Number(row.basic?.[key] ?? 0) +
        Number(row.advanced?.[key] ?? 0) +
        Number(row.advancedV2?.[key] ?? 0) +
        Number(row.complete?.[key] ?? 0) +
        Number(row.standard?.[key] ?? 0) +
        Number(row.irrigation?.[key] ?? 0) +
        Number(row.monitoring?.[key] ?? 0) +
        Number(row.preview?.[key] ?? 0)
    );
}

function getTierColumnDef(
    tier: EnrollmentTier,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    summaryStats: (rows: Row<SummaryItem>[]) => any,
) {
    const key = ENROLLMENT_TIER_KEYS[tier] as
        | "basic"
        | "advanced"
        | "advancedV2"
        | "standard"
        | "complete"
        | "irrigation"
        | "monitoring"
        | "preview";
    return {
        id: key,
        header: getEnrollmentTierLabel(tier),
        columns: [
            {
                id: `${key}_acres`,
                header: "Acres",
                accessorFn: (row) => row[key]?.acres,
                size: 80,
                footer: (props) => {
                    return (
                        <div className="text-right">
                            {summaryStats(
                                props.table.getFilteredRowModel().rows,
                            )[key].acres.toLocaleString()}
                        </div>
                    );
                },
            },
            {
                id: `${key}_retail_price`,
                header: "Retail Price",
                accessorFn: (row) => row[key]?.retail_price,
                cell: ({ row }) => {
                    return (
                        <CurrencyCell value={row.original[key]?.retail_price} />
                    );
                },
                size: 80,
            },

            {
                id: `${key}_contracted_price`,
                header: "Contracted Price",
                accessorFn: (row) => row[key]?.contracted_price,
                cell: ({ row }) => {
                    return (
                        <CurrencyCell
                            value={row.original[key]?.contracted_price}
                        />
                    );
                },
                size: 80,
            },
            {
                id: `${key}_retail_total`,
                header: "Retail Total",
                accessorFn: (row) => row[key]?.retail_total,
                cell: ({ row }) => {
                    return (
                        <CurrencyCell value={row.original[key]?.retail_total} />
                    );
                },
                size: 80,
                footer: (props) => {
                    return (
                        <CurrencyCell
                            value={
                                summaryStats(
                                    props.table.getFilteredRowModel().rows,
                                )[key].balance
                            }
                        ></CurrencyCell>
                    );
                },
            },
            {
                id: `${key}_contract_total`,
                header: "Contract Total",
                accessorFn: (row) => row.standard?.contract_total,
                cell: ({ row }) => {
                    return (
                        <CurrencyCell
                            value={row.original[key]?.contract_total}
                        />
                    );
                },
                size: 80,
            },
        ],
    } as ColumnDef<SummaryItem>;
}

function RouteComponent() {
    const { userIsSentinel } = useAuth();
    const { organizationPreviewEnabled, organizationMonitoringEnabled } =
        useFeatureFlag();
    const { useGetRequest } = useRequest();
    const contractsQuery = useGetRequest<Contract[]>({
        url: "contracts/",
        method: "get",
        queryOptions: {
            refetchOnWindowFocus: false,
        },
    });
    const fieldEnrollmentQuery = useGetRequest<Enrollment[]>({
        url: "field_enrollments/",
        method: "get",
        queryOptions: {
            refetchOnWindowFocus: false,
        },
    });
    const organizationsQuery = useGetRequest<Organization[]>({
        url: "organizations/service_providers/",
        method: "get",
        queryOptions: {
            refetchOnWindowFocus: false,
        },
    });
    const [globalFilter, setGlobalFilter] = useState("");
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

    const [existingContractId, setExistingContractId] = useState<number | null>(
        null,
    );
    const [showContractForm, setShowContractForm] = useState(false);

    const summaryStats = useCallback((rows: Row<SummaryItem>[]) => {
        return (
            rows?.reduce(
                (acc, row) => {
                    if (
                        row.original.type === "Enrollment" ||
                        row.original.type === "Unenrollment"
                    ) {
                        if (row.original.advanced) {
                            acc.advanced.balance += Number(
                                row.original.advanced.retail_total,
                            );
                            acc.advanced.acres += Number(
                                row.original.advanced.acres,
                            );
                        } else if (row.original.basic) {
                            acc.basic.balance += Number(
                                row.original.basic.retail_total,
                            );
                            acc.basic.acres += Number(row.original.basic.acres);
                        } else if (row.original.advancedV2) {
                            acc.advancedV2.balance += Number(
                                row.original.advancedV2.retail_total,
                            );
                            acc.advancedV2.acres += Number(
                                row.original.advancedV2.acres,
                            );
                        } else if (row.original.complete) {
                            acc.complete.balance += Number(
                                row.original.complete.retail_total,
                            );
                            acc.complete.acres += Number(
                                row.original.complete.acres,
                            );
                        } else if (row.original.standard) {
                            acc.standard.balance += Number(
                                row.original.standard.retail_total,
                            );
                            acc.standard.acres += Number(
                                row.original.standard.acres,
                            );
                        } else if (row.original.irrigation) {
                            acc.irrigation.balance += Number(
                                row.original.irrigation.retail_total,
                            );
                            acc.irrigation.acres += Number(
                                row.original.irrigation.acres,
                            );
                        } else if (row.original.monitoring) {
                            acc.monitoring.balance += Number(
                                row.original.monitoring.retail_total,
                            );
                            acc.monitoring.acres += Number(
                                row.original.monitoring.acres,
                            );
                        } else if (row.original.preview) {
                            acc.preview.balance += Number(
                                row.original.preview.retail_total,
                            );
                            acc.preview.acres += Number(
                                row.original.preview.acres,
                            );
                        }
                        acc.totalBalance += getTotal(
                            row.original,
                            "retail_total",
                        );
                    } else if (row.original.type === "Contract") {
                        if (row.original.advanced) {
                            acc.advanced.balance += Number(
                                row.original.advanced.retail_total,
                            );
                            acc.advanced.acres += Number(
                                row.original.advanced.acres,
                            );
                        }
                        if (row.original.basic) {
                            acc.basic.balance += Number(
                                row.original.basic.retail_total,
                            );
                            acc.basic.acres += Number(row.original.basic.acres);
                        }
                        if (row.original.advancedV2) {
                            acc.advancedV2.balance += Number(
                                row.original.advancedV2.retail_total,
                            );
                            acc.advancedV2.acres += Number(
                                row.original.advancedV2.acres,
                            );
                        }
                        if (row.original.complete) {
                            acc.complete.balance += Number(
                                row.original.complete.retail_total,
                            );
                            acc.complete.acres += Number(
                                row.original.complete.acres,
                            );
                        }
                        if (row.original.standard) {
                            acc.standard.balance += Number(
                                row.original.standard.retail_total,
                            );
                            acc.standard.acres += Number(
                                row.original.standard.acres,
                            );
                        }
                        if (row.original.irrigation) {
                            acc.irrigation.balance += Number(
                                row.original.irrigation.retail_total,
                            );
                            acc.irrigation.acres += Number(
                                row.original.irrigation.acres,
                            );
                        }
                        if (row.original.monitoring) {
                            acc.monitoring.balance += Number(
                                row.original.monitoring.retail_total,
                            );
                            acc.monitoring.acres += Number(
                                row.original.monitoring.acres,
                            );
                        }
                        if (row.original.preview) {
                            acc.preview.balance += Number(
                                row.original.preview.retail_total,
                            );
                            acc.preview.acres += Number(
                                row.original.preview.acres,
                            );
                        }
                        acc.totalBalance += getTotal(
                            row.original,
                            "retail_total",
                        );
                    }
                    return acc;
                },
                {
                    basic: {
                        acres: 0,
                        balance: 0,
                    },
                    advanced: {
                        acres: 0,
                        balance: 0,
                    },
                    advancedV2: {
                        acres: 0,
                        balance: 0,
                    },
                    complete: {
                        acres: 0,
                        balance: 0,
                    },
                    standard: {
                        acres: 0,
                        balance: 0,
                    },
                    irrigation: {
                        acres: 0,
                        balance: 0,
                    },
                    monitoring: {
                        acres: 0,
                        balance: 0,
                    },
                    preview: {
                        acres: 0,
                        balance: 0,
                    },
                    totalBalance: 0,
                },
            ) ?? {}
        );
    }, []);

    const columns = useMemo(() => {
        const cols: ColumnDef<SummaryItem>[] = [
            {
                id: "csp",
                header: "CSP",
                accessorFn: (row) => row.organization?.name,
                cell: ({ row }) => {
                    return (
                        <div className="text-left font-semibold">
                            {row.original.organization?.name}
                        </div>
                    );
                },
            },
            {
                id: "date",
                header: "Date",
                accessorFn: (row) => row.date,
                size: 100,
                footer: "Balance",
            },
            {
                id: "type",
                header: "Type",
                accessorFn: (row) => row.type,
                size: 100,
            },
            ...(organizationPreviewEnabled || userIsSentinel
                ? [getTierColumnDef(EnrollmentTier.Preview, summaryStats)]
                : []),
            getTierColumnDef(EnrollmentTier.Standard, summaryStats),
            getTierColumnDef(EnrollmentTier.Irrigation, summaryStats),
            ...(organizationMonitoringEnabled
                ? [getTierColumnDef(EnrollmentTier.Monitoring, summaryStats)]
                : []),
            getTierColumnDef(EnrollmentTier.AdvancedV2, summaryStats),
            getTierColumnDef(EnrollmentTier.Complete, summaryStats),
            {
                id: "total",
                header: "Total",
                columns: [
                    {
                        id: "total_retail_value",
                        header: "Retail Value",
                        accessorFn: (row) => getTotal(row, "retail_total"),
                        cell: ({ row }) => {
                            return (
                                <CurrencyCell
                                    value={getTotal(
                                        row.original,
                                        "retail_total",
                                    )}
                                />
                            );
                        },
                        size: 80,
                        footer: (props) => {
                            return (
                                <CurrencyCell
                                    value={
                                        summaryStats(
                                            props.table.getFilteredRowModel()
                                                .rows,
                                        ).totalBalance
                                    }
                                />
                            );
                        },
                    },

                    {
                        id: "amount_paid",
                        header: "Amount Paid",
                        accessorFn: (row) => getTotal(row, "contract_total"),
                        cell: ({ row }) => {
                            return (
                                <CurrencyCell
                                    value={getTotal(
                                        row.original,
                                        "contract_total",
                                    )}
                                />
                            );
                        },
                        size: 80,
                    },
                    {
                        id: "discount",
                        header: "Discount",
                        accessorFn: (row) => getTotal(row, "discount"),
                        cell: ({ row }) => {
                            return (
                                <CurrencyCell
                                    value={getTotal(row.original, "discount")}
                                />
                            );
                        },
                        size: 80,
                    },
                ],
            },
            {
                id: "actions",
                header: "",
                cell: ({ row }) => {
                    if (row.original.type === "Contract") {
                        return (
                            <Button
                                color="primary"
                                size="sm"
                                onPress={() => {
                                    setExistingContractId(
                                        row.original.contractId ?? null,
                                    );
                                    setShowContractForm(true);
                                }}
                                className="min-h-[1.5rem] h-[1.5rem]"
                                startContent={<FontAwesomeIcon icon={faEdit} />}
                            >
                                Edit
                            </Button>
                        );
                    }
                    return null;
                },
                size: 80,
            },
        ];

        if (!userIsSentinel) {
            cols.splice(0, 1);
            cols.pop();
        }

        return cols;
    }, [
        summaryStats,
        userIsSentinel,
        organizationPreviewEnabled,
        organizationMonitoringEnabled,
    ]);

    const activeFieldEnrollments = useMemo(() => {
        const contractsByOrganization =
            contractsQuery?.data?.reduce(
                (acc, c) => {
                    if (!c.is_active) return acc;
                    acc[c.organization] = [...(acc[c.organization] || []), c];
                    return acc;
                },
                {} as Record<number, Contract[]>,
            ) ?? {};

        return fieldEnrollmentQuery?.data?.filter((fe) => {
            const org = fe.enrolling_organization;
            if (!org) return false;
            const contracts = contractsByOrganization[org];
            if (!contracts) return false;
            const minContractDate = contracts?.reduce<Dayjs>(
                (min, c) => {
                    return dayjs(c.contract_date).isBefore(min)
                        ? dayjs(c.contract_date)
                        : min;
                },
                dayjs().add(100, "years"),
            );
            const maxContractDate = contracts?.reduce<Dayjs>(
                (max, c) => {
                    return dayjs(c.termination_date).isAfter(max)
                        ? dayjs(c.termination_date)
                        : max;
                },
                dayjs().subtract(100, "years"),
            );
            return (
                dayjs(fe.date_enrolled).isSameOrBefore(maxContractDate) &&
                dayjs(fe.date_enrolled).isSameOrAfter(minContractDate)
            );
        });
    }, [contractsQuery?.data, fieldEnrollmentQuery?.data]);

    const tableRows = useMemo(() => {
        const summaryItems: SummaryItem[] = [];

        contractsQuery?.data?.forEach((c) => {
            const advanced = c.line_items.find(
                (li) => li.enrollment_tier == EnrollmentTier.Advanced,
            );
            const basic = c.line_items.find(
                (li) => li.enrollment_tier == EnrollmentTier.Basic,
            );
            const advancedV2 = c.line_items.find(
                (li) => li.enrollment_tier == EnrollmentTier.AdvancedV2,
            );
            const complete = c.line_items.find(
                (li) => li.enrollment_tier == EnrollmentTier.Complete,
            );
            const standard = c.line_items.find(
                (li) => li.enrollment_tier == EnrollmentTier.Standard,
            );
            const irrigation = c.line_items.find(
                (li) => li.enrollment_tier == EnrollmentTier.Irrigation,
            );
            const monitoring = c.line_items.find(
                (li) => li.enrollment_tier == EnrollmentTier.Monitoring,
            );
            const preview = c.line_items.find(
                (li) => li.enrollment_tier == EnrollmentTier.Preview,
            );
            summaryItems.push({
                date: c.contract_date,
                type: "Contract",
                contractId: c.id,
                advanced,
                basic,
                advancedV2,
                complete,
                standard,
                irrigation,
                monitoring,
                preview,
                organization: organizationsQuery?.data?.find(
                    (o) => o.id === c.organization,
                ),
            });
        });

        activeFieldEnrollments?.forEach((fe) => {
            const enrollmentValue = {
                acres: -fe.acres,
                retail_price: (
                    -1 * Number(fe.retail_price_per_acre)
                ).toString(),
                contracted_price: (
                    -1 * Number(-fe.retail_price_per_acre)
                ).toString(),
                retail_total: (-1 * Number(fe.enrolled_value)).toString(),
                contract_total: (-1 * Number(fe.enrolled_value)).toString(),
                discount: "",
            };

            summaryItems.push({
                date: fe.date_enrolled,
                type: "Enrollment",
                ...(fe.enrollment_tier == EnrollmentTier.Advanced
                    ? { advanced: enrollmentValue }
                    : fe.enrollment_tier == EnrollmentTier.AdvancedV2
                      ? { advancedV2: enrollmentValue }
                      : fe.enrollment_tier == EnrollmentTier.Standard
                        ? { standard: enrollmentValue }
                        : fe.enrollment_tier == EnrollmentTier.Complete
                          ? { complete: enrollmentValue }
                          : fe.enrollment_tier == EnrollmentTier.Irrigation
                            ? { irrigation: enrollmentValue }
                            : fe.enrollment_tier == EnrollmentTier.Monitoring
                              ? { monitoring: enrollmentValue }
                              : fe.enrollment_tier == EnrollmentTier.Preview
                                ? { preview: enrollmentValue }
                                : { basic: enrollmentValue }),

                organization: organizationsQuery?.data?.find(
                    (o) => o.id === fe.enrolling_organization,
                ),
            });

            if (fe.refunded_on) {
                const UnenrollmentValue = {
                    acres: fe.acres,
                    retail_price: fe.retail_refund_price_per_acre,
                    contracted_price: fe.retail_refund_price_per_acre,
                    retail_total: fe.refunded_value,
                    contract_total: fe.refunded_value,
                    discount: "",
                };

                summaryItems.push({
                    date: fe.refunded_on,
                    type: "Unenrollment",
                    ...(fe.enrollment_tier == EnrollmentTier.Advanced
                        ? { advanced: UnenrollmentValue }
                        : fe.enrollment_tier == EnrollmentTier.AdvancedV2
                          ? { advancedV2: UnenrollmentValue }
                          : fe.enrollment_tier == EnrollmentTier.Standard
                            ? { standard: UnenrollmentValue }
                            : fe.enrollment_tier == EnrollmentTier.Complete
                              ? { complete: UnenrollmentValue }
                              : fe.enrollment_tier == EnrollmentTier.Irrigation
                                ? { irrigation: UnenrollmentValue }
                                : fe.enrollment_tier ==
                                    EnrollmentTier.Monitoring
                                  ? { monitoring: UnenrollmentValue }
                                  : fe.enrollment_tier == EnrollmentTier.Preview
                                    ? { preview: UnenrollmentValue }
                                    : { basic: UnenrollmentValue }),
                    organization: organizationsQuery?.data?.find(
                        (o) => o.id === fe.enrolling_organization,
                    ),
                });
            }
        });

        return sortArrayByKey(summaryItems, "date", "desc");
    }, [
        contractsQuery?.data,
        activeFieldEnrollments,
        organizationsQuery?.data,
    ]);

    const table = useReactTable({
        columns,
        data: tableRows,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            globalFilter,
            columnFilters,
        },
        initialState: {
            sorting: [
                {
                    id: "date",
                    desc: true,
                },
            ],
        },
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: (row, columnId, filterValue) => {
            const value = row.getValue(columnId);
            return String(value)
                .toLowerCase()
                .includes(filterValue.toLowerCase());
        },
        onColumnFiltersChange: setColumnFilters,
    });

    const { rows } = table.getRowModel();

    const headersById = useMemo(() => {
        return table
            .getHeaderGroups()
            .map((group) => group.headers.map((header) => header))
            .flat()
            .reduce(
                (acc, header) => {
                    acc[header.id] = header;
                    return acc;
                },
                {} as Record<string, Header<SummaryItem, unknown>>,
            );
    }, [table]);

    const ref = useRef<HTMLDivElement>(null);

    const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
        count: rows.length,
        estimateSize: () => 33, //estimate row height for accurate scrollbar dragging
        getScrollElement: () => ref.current,
        //measure dynamic row height, except in firefox because it measures table border height incorrectly
        measureElement:
            typeof window !== "undefined" &&
            navigator.userAgent.indexOf("Firefox") === -1
                ? (element) => element?.getBoundingClientRect().height
                : undefined,
        overscan: 20,
    });
    const { rows: prefilteredRows } = table.getPreFilteredRowModel();

    const filterOptions = useMemo(() => {
        const filters = {
            csp: undefined,
        };
        if (userIsSentinel) {
            return {
                csp: getColumnValues("csp", prefilteredRows)
                    .filter((csp) => !!csp)
                    .map((csp) => {
                        return (
                            <AutocompleteItem
                                key={csp}
                                textValue={csp ?? "N/A"}
                            >
                                {csp}
                            </AutocompleteItem>
                        );
                    }),
            };
        }
        return filters;
    }, [userIsSentinel, prefilteredRows]);

    const footerGroup = table.getFooterGroups();
    return (
        <div className="p-2 flex flex-col  h-full overflow-hidden ">
            <div className="flex flex-row justify-between items-center">
                <div className="flex flex-row gap-2 items-center">
                    <Input
                        startContent={<FontAwesomeIcon icon={faSearch} />}
                        value={globalFilter}
                        size="sm"
                        isClearable
                        onClear={() => setGlobalFilter("")}
                        onChange={(e) =>
                            setGlobalFilter(String(e.target.value))
                        }
                        placeholder={"Search.."}
                    />
                    {userIsSentinel && (
                        <Autocomplete
                            color={
                                headersById["csp"].column.getFilterValue()
                                    ? "primary"
                                    : "default"
                            }
                            aria-label="Filter"
                            className="w-full pointer-events-auto"
                            size="sm"
                            placeholder="CSP"
                            showScrollIndicators={false}
                            selectedKey={
                                headersById[
                                    "csp"
                                ].column.getFilterValue() as string
                            }
                            onSelectionChange={(e) => {
                                headersById["csp"].column.setFilterValue(e);
                            }}
                        >
                            {filterOptions?.csp ?? (
                                <AutocompleteItem>N/A</AutocompleteItem>
                            )}
                        </Autocomplete>
                    )}
                </div>
                {userIsSentinel && (
                    <div className="flex flex-row gap-2 items-center">
                        <Button
                            color="secondary"
                            onPress={() => {
                                setExistingContractId(null);
                                setShowContractForm(true);
                            }}
                            size="sm"
                            startContent={<FontAwesomeIcon icon={faPlus} />}
                        >
                            Add Contract
                        </Button>
                    </div>
                )}
            </div>
            <div
                ref={ref}
                className="overflow-y-scroll text-xs bg-white w-full mt-2 h-[600px]"
            >
                <table className="w-full min-h-full block">
                    <thead
                        style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                        }}
                        className="bg-gray-100 w-full"
                    >
                        {table.getHeaderGroups().map((group) => (
                            <tr
                                key={group.id}
                                className="border-b  border-gray-700 w-full flex flex-row items-center justify-items-center"
                            >
                                {group.headers.map((header) => {
                                    return (
                                        <th
                                            aria-label={
                                                header.column.columnDef.header?.toString() ??
                                                "column header"
                                            }
                                            key={header.id}
                                            style={{
                                                width: header.getSize(),
                                            }}
                                            colSpan={header.colSpan}
                                            className="pointer-events-none text-right "
                                        >
                                            {header.isPlaceholder ? null : (
                                                <div
                                                    onClick={header.column.getToggleSortingHandler()}
                                                    className={classNames({
                                                        "hover:bg-gray-200 rounded-lg p-1 text-xs pointer-events-auto w-full flex text-start":
                                                            true,
                                                        "cursor-pointer":
                                                            header.column.getCanSort(),
                                                    })}
                                                >
                                                    {flexRender(
                                                        header.column.columnDef
                                                            .header,
                                                        header.getContext(),
                                                    )}
                                                    {header.column.getCanSort() &&
                                                        header.column.getIsSorted() && (
                                                            <FontAwesomeIcon
                                                                className="ml-2 text-primary-500"
                                                                icon={
                                                                    header.column.getIsSorted() ===
                                                                    "asc"
                                                                        ? faArrowUpShortWide
                                                                        : faArrowDownShortWide
                                                                }
                                                                size="sm"
                                                            />
                                                        )}
                                                </div>
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody
                        style={{
                            height: `${rowVirtualizer.getTotalSize()}px`,
                            position: "relative",
                            width: table.getCenterTotalSize(),
                        }}
                    >
                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                            const row = rows[
                                virtualRow.index
                            ] as Row<SummaryItem>;
                            return (
                                <tr
                                    data-index={virtualRow.index}
                                    ref={(node) =>
                                        rowVirtualizer.measureElement(node)
                                    }
                                    key={`${row.id}-${virtualRow.index}`}
                                    style={{
                                        position: "absolute",
                                        transform: `translateY(${virtualRow.start}px)`,
                                        width: "100%",
                                    }}
                                    className=" border-gray-700 text-right"
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <td
                                                key={cell.id}
                                                style={{
                                                    width: cell.column.getSize(),
                                                }}
                                                className="border-r border-b border-gray-700 p-[0.25rem]"
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot className="sticky bottom-0  bg-gray-100 z-20">
                        {footerGroup.map((footerGroup) => (
                            <tr
                                key={footerGroup.id}
                                className="border-t border-gray-700 flex flex-row items-center justify-items-center"
                                style={{
                                    position: "sticky",
                                    bottom: 0,
                                    zIndex: 1,
                                }}
                            >
                                {footerGroup.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        style={{
                                            width: header.getSize(),
                                        }}
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                  header.column.columnDef
                                                      .footer,
                                                  header.getContext(),
                                              )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </tfoot>
                </table>
                {contractsQuery.isFetching && (
                    <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
                        <Spinner size="lg" color="primary" label="Loading..." />
                    </div>
                )}
            </div>
            {showContractForm && (
                <ContractForm
                    existingContract={contractsQuery.data?.find(
                        (c) => c.id === existingContractId,
                    )}
                    organizations={organizationsQuery.data ?? []}
                    onClose={(shouldRefetch) => {
                        setShowContractForm(false);
                        setExistingContractId(null);
                        if (shouldRefetch) {
                            contractsQuery.refetch();
                        }
                    }}
                />
            )}
        </div>
    );
}
